import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { isNil } from "lodash";
import { Observable, merge, of } from "rxjs";
import { distinctUntilChanged, filter, map, tap } from "rxjs/operators";
import { LeveransvirkesvolymMediatorService } from "~app/features/virkeskop/services/leveransvirkesvolym-mediator.service";
import { VirkeskostnaderMediatorService } from "~app/features/virkeskop/services/virkeskostnader-mediator.service";
import { AtgardVolymForm } from "../atgard-volym-forms";

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: "tr[rask-atgard-volym-row]",
    templateUrl: "./atgard-volym-row.component.html",
    styleUrls: ["./atgard-volym-row.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputNumberComponent,
        AsyncPipe
    ]
})
export class AtgardVolymRowComponent implements OnInit {
    @Input() volymForm: AtgardVolymForm;

    protected changeDetection$: Observable<any>;

    private readonly leveransvirkesvolymMediatorService = inject(LeveransvirkesvolymMediatorService);
    private readonly virkeskostnaderMediatorService = inject(VirkeskostnaderMediatorService);

    ngOnInit(): void {
        const init$ = of();

        const virkeskostnader$ = this.leveransvirkesvolymMediatorService.virkeskostnader$.pipe(
            map(virkeskostnader => virkeskostnader.find(x => x.guid === this.volymForm.controls.virkeskostnadGuid.value)),
            filter(virkeskostnad => !isNil(virkeskostnad)),
            tap(virkeskostnad => {
                this.volymForm.patchValue({
                    mattslag: virkeskostnad.mattslag,
                    handelssortimentnamn: virkeskostnad.handelssortimentNamn ?? virkeskostnad.handelssortimentsgruppNamn
                }, { emitEvent: false,
                    onlySelf: true });
            })
        );

        const virkeskostnadUpdated$ = this.virkeskostnaderMediatorService.virkeskostnadUpdated$.pipe(
            filter(virkeskostnad => virkeskostnad.virkeskostnad.guid === this.volymForm.controls.virkeskostnadGuid.value),
            tap(virkeskostnad => {
                this.volymForm.patchValue({
                    mattslag: virkeskostnad.virkeskostnad.mattslag
                }, { emitEvent: false,
                    onlySelf: true });
            })
        );

        const reload$ = this.leveransvirkesvolymMediatorService.leveransvirkesvolymer$.pipe(
            map((volymer) => volymer.find(vol =>
                vol.virkeskostnadGuid === this.volymForm.controls.virkeskostnadGuid.value &&
                vol.virkeskopsatgardLeveransvirkeGuid === this.volymForm.controls.atgardGuid.value)),
            filter(volym => !isNil(volym)),
            tap(volym => {
                this.volymForm.patchValue({
                    kvantitet: volym.kvantitet
                }, { emitEvent: false,
                    onlySelf: true });
            }));

        const valueChanges$ = this.volymForm.valueChanges.pipe(
            distinctUntilChanged(),
            tap(values => this.leveransvirkesvolymMediatorService.updateVolymSrc.next({
                virkeskostnadGuid: this.volymForm.controls.virkeskostnadGuid.value,
                virkeskopsatgardLeveransvirkeGuid: this.volymForm.controls.atgardGuid.value,
                id: 0,
                kvantitet: values.kvantitet,
                handelssortimentNamn: this.volymForm.controls.handelssortimentnamn.value,
                mattslag: this.volymForm.controls.mattslag.value,
                handelssortimentId: this.volymForm.controls.handelssortimentId.value,
                handelssortimentsgruppSekvensnummer: this.volymForm.controls.handelssortimentsgruppSekvensnummer.value
            }))
        );

        this.changeDetection$ = merge(reload$, valueChanges$, init$, virkeskostnadUpdated$, virkeskostnader$);
    }
}
